/**
 * Horse Search Component
 *
 * Alpine.js component for the search/listings page.
 * Handles filtering, sorting, pagination, and display of horse listings.
 *
 * @package Lovasok_Marketplace
 */

import { LovasokAPI } from '../utils/api.js';
import { formatPrice } from '../utils/formatters.js';

export default (filterLabels = {}) => ({
    // State
    horses: [],
    loading: true,
    paging: false,
    _pageCache: null,
    currentPage: 1,
    totalPages: 1,
    totalResults: 0,

    // Filters
    filters: {
        search: '',
        priceMin: 0,
        priceMax: 80000000,
        gender: '',
        ageMin: '',
        ageMax: '',
        listingType: '',
        datePosted: 'all',
        training: '',
        breed: '',
        suitability: '',
        color: '',
        county: '',
        isTradeable: false,
        hasPhoto: false,
        hasPassport: false,
        hasRaceNumber: false,
        competes: false,
        isPurebred: false,
        hasVideo: false,
        hasSchoolExam: false,
        author: '',
        authorName: '',
    },

    // UI State
    sortBy: 'newest',
    viewMode: 'grid',
    isMobileFiltersOpen: false,
    _mobileFiltersTrigger: null,
    isAdvancedOpen: false,
    /** URL ?highlight= — 'premium' | 'featured' */
    highlightFilter: '',

    // Sort labels
    sortOptions: {
        'newest': 'Legújabb',
        'oldest': 'Legrégebbi',
        'price-asc': 'Ár (növekvő)',
        'price-desc': 'Ár (csökkenő)',
        'age-asc': 'Kor (növekvő)',
        'age-desc': 'Kor (csökkenő)',
    },

    // Computed: Filtered horses (server-side filtering)
    get filteredHorses() {
        return this.horses;
    },

    // Computed: Pagination page range for UI
    get paginationRange() {
        const total = this.totalPages;
        const current = this.currentPage;
        const range = [];

        const pushPage = (n) => range.push({ key: `p${n}`, label: String(n), page: n });
        const pushGap = (id) => range.push({ key: id, label: '…', page: null });

        if (total <= 7) {
            for (let i = 1; i <= total; i++) pushPage(i);
            return range;
        }

        pushPage(1);
        if (current > 3) pushGap('gap-start');

        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        for (let i = start; i <= end; i++) pushPage(i);

        if (current < total - 2) pushGap('gap-end');
        pushPage(total);

        return range;
    },

    get activeFilterChips() {
        const chips = [];
        const add = (key, label, value) => chips.push({ key, label, value });
        const labelFor = (group, value) => filterLabels[group]?.[value] || value;
        const formatNumber = (value) => new Intl.NumberFormat('hu-HU').format(Number(value));

        if (this.filters.search) add('search', 'Keresés', this.filters.search);

        const hasMinPrice = Number(this.filters.priceMin) > 0;
        const hasMaxPrice = Number(this.filters.priceMax) > 0 && Number(this.filters.priceMax) < 80000000;
        if (hasMinPrice || hasMaxPrice) {
            const value = hasMinPrice && hasMaxPrice
                ? `${formatNumber(this.filters.priceMin)}–${formatNumber(this.filters.priceMax)} Ft`
                : hasMinPrice
                    ? `${formatNumber(this.filters.priceMin)} Ft-tól`
                    : `${formatNumber(this.filters.priceMax)} Ft-ig`;
            add('price', 'Ár', value);
        }

        if (this.filters.gender) add('gender', 'Nem', labelFor('gender', this.filters.gender));

        const hasMinAge = this.filters.ageMin !== null && this.filters.ageMin !== '' && Number(this.filters.ageMin) > 0;
        const hasMaxAge = this.filters.ageMax !== null && this.filters.ageMax !== '' && Number(this.filters.ageMax) < 30;
        if (hasMinAge || hasMaxAge) {
            const value = hasMinAge && hasMaxAge
                ? `${this.filters.ageMin}–${this.filters.ageMax} év`
                : hasMinAge ? `${this.filters.ageMin} évtől` : `${this.filters.ageMax} évig`;
            add('age', 'Kor', value);
        }

        if (this.filters.listingType) add('listingType', 'Típus', labelFor('listingType', this.filters.listingType));
        if (this.filters.datePosted !== 'all') add('datePosted', 'Feladva', labelFor('datePosted', this.filters.datePosted));
        if (this.filters.county) add('county', 'Megye', labelFor('county', this.filters.county));
        if (this.filters.training) add('training', 'Képzettség', labelFor('training', this.filters.training));
        if (this.filters.breed) add('breed', 'Fajta', labelFor('breed', this.filters.breed));
        if (this.filters.suitability) add('suitability', 'Alkalmasság', labelFor('suitability', this.filters.suitability));
        if (this.filters.color) add('color', 'Szín', labelFor('color', this.filters.color));
        if (this.filters.isTradeable) add('isTradeable', 'Opció', 'Cserélhető');
        if (this.filters.hasPhoto) add('hasPhoto', 'Opció', 'Fotóval');
        if (this.filters.hasPassport) add('hasPassport', 'Opció', 'Lóútlevéllel');
        if (this.filters.hasRaceNumber) add('hasRaceNumber', 'Opció', 'Startszámmal');
        if (this.filters.competes) add('competes', 'Opció', 'Versenyez');
        if (this.filters.isPurebred) add('isPurebred', 'Opció', 'Tisztavérű');
        if (this.filters.hasVideo) add('hasVideo', 'Opció', 'Videóval');
        if (this.filters.hasSchoolExam) add('hasSchoolExam', 'Opció', 'Iskolaló vizsgával');
        if (this.highlightFilter) add('highlight', 'Kiemelés', labelFor('highlight', this.highlightFilter));
        if (this.filters.author) add('author', 'Hirdető', this.filters.authorName || `#${this.filters.author}`);

        return chips;
    },

    get activeFiltersCount() {
        return this.activeFilterChips.length;
    },

    // Initialize
    async init() {
        const initId = (this._initId = (this._initId || 0) + 1);
        const urlParams = new URLSearchParams(window.location.search);
		const isEnabled = (key) => urlParams.get(key) === '1';

        const q = (urlParams.get('search') || urlParams.get('q') || urlParams.get('s') || '').trim();
        if (q) {
            this.filters.search = q;
        }

        const breed = (urlParams.get('breed') || '').trim();
        if (breed) {
            this.filters.breed = breed;
        }

        const county = (urlParams.get('county') || '').trim();
        if (county) {
            this.filters.county = county;
        }

        const listingType = (urlParams.get('listing_type') || urlParams.get('listingType') || '').trim();
        if (listingType) {
            this.filters.listingType = listingType;
        }

        const color = (urlParams.get('color') || '').trim();
        if (color) {
            this.filters.color = color;
        }

        const training = (urlParams.get('training') || '').trim();
        if (training) {
            this.filters.training = training;
        }

        const suitability = (urlParams.get('suitability') || '').trim();
        if (suitability) {
            this.filters.suitability = suitability;
        }

		const priceMin = Number(urlParams.get('min_price'));
		if (Number.isFinite(priceMin) && priceMin > 0) {
			this.filters.priceMin = priceMin;
		}

		const priceMax = Number(urlParams.get('max_price'));
		if (Number.isFinite(priceMax) && priceMax > 0 && priceMax < 80000000) {
			this.filters.priceMax = priceMax;
		}

		this.filters.gender = (urlParams.get('gender') || '').trim();
		this.filters.ageMin = (urlParams.get('min_age') || '').trim();
		this.filters.ageMax = (urlParams.get('max_age') || '').trim();
		this.filters.datePosted = (urlParams.get('date_posted') || 'all').trim();
		this.filters.isTradeable = isEnabled('is_tradeable');
		this.filters.hasPhoto = isEnabled('has_photo');
		this.filters.hasPassport = isEnabled('has_passport');
		this.filters.hasRaceNumber = isEnabled('has_race_number');
		this.filters.competes = isEnabled('competes');
		this.filters.isPurebred = isEnabled('is_purebred');
		this.filters.hasVideo = isEnabled('has_video');
		this.filters.hasSchoolExam = isEnabled('has_school_exam');

		const sort = (urlParams.get('sort') || '').trim();
		if (this.sortOptions[sort]) {
			this.sortBy = sort;
		}

		const requestedPage = parseInt(urlParams.get('oldal') || '1', 10);
		this.currentPage = Number.isFinite(requestedPage) && requestedPage > 1 ? requestedPage : 1;

        const highlight = (urlParams.get('highlight') || '').trim().toLowerCase();
        if (highlight === 'premium') {
            this.highlightFilter = 'premium';
        } else if (highlight === 'featured' || highlight === '1') {
            this.highlightFilter = 'featured';
        }

        const author = (urlParams.get('seller') || '').trim();
        if (author) {
            this.filters.author = author;
        }
        const authorName = (urlParams.get('seller_name') || '').trim();
        if (authorName) {
            this.filters.authorName = decodeURIComponent(authorName.replace(/\+/g, ' '));
        }

        if (
            this.filters.training
            || this.filters.breed
            || this.filters.suitability
            || this.filters.color
            || this.filters.isTradeable
            || this.filters.hasPhoto
            || this.filters.hasPassport
            || this.filters.hasRaceNumber
            || this.filters.competes
            || this.filters.isPurebred
            || this.filters.hasVideo
            || this.filters.hasSchoolExam
        ) {
            this.isAdvancedOpen = true;
        }

        await this.fetchHorses();
		if (initId !== this._initId) {
			return;
		}
		if (this.currentPage > this.totalPages && this.totalPages > 0) {
			this.currentPage = this.totalPages;
			this.syncUrl();
			await this.fetchHorses();
		}

        if (this.filters.author && !this.filters.authorName && this.horses[0]?.authorName) {
            this.filters.authorName = this.horses[0].authorName;
        }

        // Watch for filter changes with Debounce — reset to page 1
        let debounceTimer;
        let previousFilters = JSON.stringify(this.filters);
        this.$watch('filters', () => {
            clearTimeout(debounceTimer);

			if (this.filters.ageMin === null) this.filters.ageMin = '';
			if (this.filters.ageMax === null) this.filters.ageMax = '';

			const currentFilters = JSON.stringify(this.filters);
			const previousValues = JSON.parse(previousFilters);
			const changedFilters = Object.keys(this.filters).filter(
				(key) => this.filters[key] !== previousValues[key]
			);
			previousFilters = currentFilters;
			const isOnlyAgeChanging = changedFilters.length > 0
				&& changedFilters.every((key) => key === 'ageMin' || key === 'ageMax');

			this.syncUrl();
            debounceTimer = setTimeout(() => {
                this.currentPage = 1;
				this.syncUrl();
                this.fetchHorses();
            }, isOnlyAgeChanging ? 1500 : 500);
        }, { deep: true });

        // Watch sort change — reset to page 1
        this.$watch('sortBy', () => {
            this.currentPage = 1;
			this.syncUrl();
            this.fetchHorses();
        });

        this.$watch('highlightFilter', () => {
            this.currentPage = 1;
            this.syncUrl();
            this.fetchHorses();
        });

        this.$watch('isMobileFiltersOpen', (isOpen) => {
            document.body.classList.toggle('filters-open', isOpen);
        });
    },

	syncUrl() {
		if (typeof window === 'undefined') return;

		const url = new URL(window.location.href);
		const setParam = (key, value, defaultValue = '') => {
			if (value !== '' && value !== null && value !== false && value !== defaultValue) {
				url.searchParams.set(key, String(value));
			} else {
				url.searchParams.delete(key);
			}
		};

		setParam('search', this.filters.search);
		const priceMin = Number(this.filters.priceMin);
		const priceMax = Number(this.filters.priceMax);
		setParam('min_price', priceMin > 0 ? priceMin : '');
		setParam('max_price', priceMax > 0 && priceMax < 80000000 ? priceMax : '');
		setParam('gender', this.filters.gender);
		setParam('min_age', this.filters.ageMin === null ? '' : this.filters.ageMin);
		setParam('max_age', this.filters.ageMax === null ? '' : this.filters.ageMax);
		setParam('listing_type', this.filters.listingType);
		setParam('date_posted', this.filters.datePosted, 'all');
		setParam('training', this.filters.training);
		setParam('breed', this.filters.breed);
		setParam('suitability', this.filters.suitability);
		setParam('color', this.filters.color);
		setParam('county', this.filters.county);
		setParam('is_tradeable', this.filters.isTradeable ? '1' : '');
		setParam('has_photo', this.filters.hasPhoto ? '1' : '');
		setParam('has_passport', this.filters.hasPassport ? '1' : '');
		setParam('has_race_number', this.filters.hasRaceNumber ? '1' : '');
		setParam('competes', this.filters.competes ? '1' : '');
		setParam('is_purebred', this.filters.isPurebred ? '1' : '');
		setParam('has_video', this.filters.hasVideo ? '1' : '');
		setParam('has_school_exam', this.filters.hasSchoolExam ? '1' : '');
		setParam('highlight', this.highlightFilter);
		setParam('seller', this.filters.author);
		setParam('seller_name', this.filters.authorName);
		setParam('sort', this.sortBy, 'newest');
		setParam('oldal', this.currentPage > 1 ? this.currentPage : '');
		url.searchParams.delete('page');
		url.searchParams.delete('q');
		url.searchParams.delete('s');
		url.searchParams.delete('listingType');

		window.history.replaceState(window.history.state, '', url);
	},

    openMobileFilters(trigger = null) {
        this._mobileFiltersTrigger = trigger;
        this.isMobileFiltersOpen = true;
        this.$nextTick(() => this.$refs.mobileFiltersDialog?.focus());
    },

    closeMobileFilters() {
        this.isMobileFiltersOpen = false;
        this.$nextTick(() => this._mobileFiltersTrigger?.focus());
    },

    trapMobileFiltersFocus(event) {
        if (!this.isMobileFiltersOpen) return;

        const focusable = [...this.$refs.mobileFiltersDialog.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )].filter((element) => element.offsetParent !== null);

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    },

    buildListParams(page = this.currentPage) {
        const params = {
            page,
            per_page: 12,
            sort: this.sortBy,
        };

        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.priceMin > 0) params.min_price = this.filters.priceMin;
        if (this.filters.priceMax < 80000000) params.max_price = this.filters.priceMax;
        if (this.filters.gender) params.gender = this.filters.gender;
        if (this.filters.ageMin !== null && this.filters.ageMin !== '' && Number(this.filters.ageMin) > 0) params.min_age = this.filters.ageMin;
        if (this.filters.ageMax !== null && this.filters.ageMax !== '' && Number(this.filters.ageMax) < 30) params.max_age = this.filters.ageMax;
        if (this.filters.listingType) params.listing_type = this.filters.listingType;
        if (this.filters.training) params.training = this.filters.training;
        if (this.filters.breed) params.breed = this.filters.breed;
        if (this.filters.suitability) params.suitability = this.filters.suitability;
        if (this.filters.color) params.color = this.filters.color;
        if (this.filters.county) params.county = this.filters.county;
        if (this.filters.isTradeable) params.is_tradeable = '1';
        if (this.filters.hasPhoto) params.has_photo = '1';
        if (this.filters.hasPassport) params.has_passport = '1';
        if (this.filters.hasRaceNumber) params.has_race_number = '1';
        if (this.filters.competes) params.competes = '1';
        if (this.filters.isPurebred) params.is_purebred = '1';
        if (this.filters.hasVideo) params.has_video = '1';
        if (this.filters.hasSchoolExam) params.has_school_exam = '1';
        if (this.filters.datePosted && this.filters.datePosted !== 'all') {
            params.date_posted = this.filters.datePosted;
        }
        if (this.highlightFilter === 'premium') {
            params.highlight_type = 'premium';
        } else if (this.highlightFilter === 'featured') {
            params.featured = '1';
        }
        if (this.filters.author) {
            params.author = this.filters.author;
        }

        return params;
    },

    listCacheKey(params) {
        return JSON.stringify(params);
    },

    rememberListPage(key, payload) {
        if (!this._pageCache) this._pageCache = new Map();
        if (this._pageCache.has(key)) this._pageCache.delete(key);
        this._pageCache.set(key, payload);
        while (this._pageCache.size > 8) {
            const oldest = this._pageCache.keys().next().value;
            this._pageCache.delete(oldest);
        }
    },

    applyListPayload(data) {
        this.horses = data.horses || [];
        if (this.filters.author && !this.filters.authorName && this.horses[0]?.authorName) {
            this.filters.authorName = this.horses[0].authorName;
        }
        this.totalPages = data.pages || 1;
        this.totalResults = data.total || 0;
    },

    prefetchNextPage() {
        const next = this.currentPage + 1;
        if (next > this.totalPages) return;

        const params = this.buildListParams(next);
        const key = this.listCacheKey(params);
        if (this._pageCache?.has(key) || this._prefetchKey === key) return;

        this._prefetchKey = key;
        LovasokAPI.get('horses', params)
            .then((data) => {
                if (this._prefetchKey === key) this._prefetchKey = null;
                this.rememberListPage(key, {
                    horses: data.horses || [],
                    pages: data.pages || 1,
                    total: data.total || 0,
                });
            })
            .catch(() => {
                if (this._prefetchKey === key) this._prefetchKey = null;
            });
    },

    // Fetch horses from REST API with Filters
    async fetchHorses() {
        const params = this.buildListParams();
        const cacheKey = this.listCacheKey(params);
        const cached = this._pageCache?.get(cacheKey);
        if (cached) {
            if (this._fetchAbort) {
                this._fetchAbort.abort();
            }
            this._fetchGeneration = (this._fetchGeneration || 0) + 1;
            this.applyListPayload(cached);
            this.loading = false;
            this.paging = false;
            this.prefetchNextPage();
            return true;
        }

        // Cancel in-flight search when filters change quickly (avoids race + wasted load).
        if (this._fetchAbort) {
            this._fetchAbort.abort();
        }
        this._fetchAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const fetchGeneration = (this._fetchGeneration = (this._fetchGeneration || 0) + 1);

        // Keep the current cards on screen so the footer does not jump up
        // while the next page is loading. The skeleton is only for the first paint.
        const keepVisible = this.horses.length > 0;
        if (keepVisible) {
            this.paging = true;
        } else {
            this.loading = true;
        }
        try {
            const data = await LovasokAPI.get('horses', params, {
                signal: this._fetchAbort ? this._fetchAbort.signal : undefined,
            });

            // Stale response after a newer fetch started — ignore.
            if (fetchGeneration !== this._fetchGeneration) {
                return false;
            }

            const payload = {
                horses: data.horses || [],
                pages: data.pages || 1,
                total: data.total || 0,
            };
            this.rememberListPage(cacheKey, payload);
            this.applyListPayload(payload);
            this.prefetchNextPage();
            return true;

        } catch (error) {
            if (error?.name === 'AbortError') {
                return false;
            }
            if (fetchGeneration !== this._fetchGeneration) {
                return false;
            }
            console.error('Error fetching horses:', error);
            this.horses = [];
            this.totalPages = 1;
            this.totalResults = 0;
            return false;
        } finally {
            if (fetchGeneration === this._fetchGeneration) {
                this.loading = false;
                this.paging = false;
            }
        }
    },

    // Reset all filters
    resetFilters() {
        this.filters = {
            search: '',
            priceMin: 0,
            priceMax: 80000000,
            gender: '',
            ageMin: '',
            ageMax: '',
            listingType: '',
            datePosted: 'all',
            training: '',
            breed: '',
            suitability: '',
            color: '',
            county: '',
            isTradeable: false,
            hasPhoto: false,
            hasPassport: false,
            hasRaceNumber: false,
            competes: false,
            isPurebred: false,
            hasVideo: false,
            hasSchoolExam: false,
            author: '',
            authorName: '',
        };
        this.highlightFilter = '';
        this.currentPage = 1;
        // Close the mobile filter drawer so the reset is visible immediately on mobile.
        this.closeMobileFilters();
        this.fetchHorses();
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('seller');
            url.searchParams.delete('seller_name');
            window.history.replaceState({}, '', url);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    },

    removeActiveFilter(key) {
        const rangeFilters = {
            price: ['priceMin', 'priceMax'],
            age: ['ageMin', 'ageMax'],
        };

        if (rangeFilters[key]) {
            rangeFilters[key].forEach((field) => {
                this.filters[field] = field === 'priceMin' ? 0 : field === 'priceMax' ? 80000000 : '';
            });
            return;
        }

        if (key === 'author') {
            this.filters.author = '';
            this.filters.authorName = '';
            return;
        }

        if (key === 'highlight') {
            this.highlightFilter = '';
            return;
        }

        const booleanFilters = ['isTradeable', 'hasPhoto', 'hasPassport', 'hasRaceNumber', 'competes', 'isPurebred', 'hasVideo', 'hasSchoolExam'];
        this.filters[key] = booleanFilters.includes(key) ? false : key === 'datePosted' ? 'all' : '';
    },

    scrollToListings() {
        const anchor = document.getElementById('lmk-search-page-header')
            || document.getElementById('lmk-horseface-page');
        const top = anchor
            ? Math.max(0, anchor.getBoundingClientRect().top + window.scrollY)
            : 0;
        window.scrollTo({ top, behavior: 'auto' });
    },

    // Pagination methods
    async goToPage(page) {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
            this.currentPage = page;
			this.syncUrl();
            // Scroll while the current cards still hold the page height.
            this.scrollToListings();
            await this.fetchHorses();
        }
    },

    prevPage() {
        this.goToPage(this.currentPage - 1);
    },

    nextPage() {
        this.goToPage(this.currentPage + 1);
    },

    async toggleFavorite(horseId) {
        const params = window.lovasokParams || {};
        const home = (params.homeUrl || '').replace(/\/$/, '');
        const loginUrl = `${home}/bejelentkezes`;
        const targetId = Number(horseId);

        try {
            const data = await LovasokAPI.post(`horses/${horseId}/favorite`);
            const horse = this.horses.find(h => Number(h.id) === targetId);
            if (horse) {
                horse.isFavorited = data.favorited;
            }
        } catch (e) {
            const msg = e?.message || '';
            if (msg.includes('401') || msg.includes('403') || msg.includes('jogosultság')) {
                window.location.href = loginUrl;
                return;
            }
            if (msg.includes('Saját hirdetés')) {
                alert('Saját hirdetésedet nem adhatod kedvencekhez.');
            }
            console.error('Favorite toggle failed:', e);
        }
    },

    formatPrice(price, priceStatus = '', priceCategory = '', currency = 'Ft') {
        return formatPrice(price, true, priceStatus, priceCategory, currency);
    }
});
